import React from 'react';
import { auth } from '../firebase';

/**
 * Save a snapshot of the current document state
 * @param {Object} documentData - The document data to save
 * @param {string} documentData.type - 'space' or 'page'
 * @param {string} documentData.name - Document name
 * @param {string} documentData.content - Document content
 * @param {string} documentData.description - Document description
 * @param {string} documentData.parentPage - Parent page (for pages only)
 * @param {boolean} documentData.isPublic - Whether document is public
 * @param {boolean} documentData.completed - Whether document is completed
 */
export const saveDocumentSnapshot = async (documentData) => {
    try {
        const user = auth.currentUser;
        if (!user) {
            return false;
        }

        const token = await user.getIdToken();

        // Prepare snapshot data
        const snapshotPayload = {
            document_type: documentData.type,
            document_name: documentData.name,
            content: documentData.content,
            description: documentData.description,
            is_public: documentData.isPublic,
            completed: documentData.completed,
        };

        // Add parent_page for pages
        if (documentData.type === 'page' && documentData.parentPage) {
            snapshotPayload.parent_page = documentData.parentPage;
        }

        const response = await fetch(`https://bluebirddocumentationadmin.pythonanywhere.com/snapshots/personal/save/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Auth-Token': `Bearer ${token}`,
                'Authorization': `${token}`,
                'Requesting-User-Uid': user.uid
            },
            body: JSON.stringify(snapshotPayload)
        });

        if (response.ok) {
            await response.json();
            return true;
        } else {
            console.error('Failed to save snapshot:', response.status, response.statusText);
            return false;
        }
    } catch (error) {
        console.error('Error saving snapshot:', error);
        return false;
    }
};

/**
 * Custom hook for automatic snapshot saving
 * @param {Object} documentData - Current document data
 * @param {number} frequencyMinutes - How often to save snapshots (in minutes)
 * @param {boolean} enabled - Whether auto-saving is enabled
 */
export const useAutoSnapshot = (documentData, frequencyMinutes = 5, enabled = true) => {
    const intervalRef = React.useRef(null);
    const lastSnapshotRef = React.useRef(null);

    const startAutoSnapshot = React.useCallback(() => {
        if (!enabled || !documentData.name || !documentData.content) {
            return;
        }

        // Clear existing interval
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        // Set up new interval
        intervalRef.current = setInterval(async () => {
            // Only save if content has changed since last snapshot
            const currentContent = JSON.stringify(documentData);
            if (currentContent !== lastSnapshotRef.current) {
                const success = await saveDocumentSnapshot(documentData);
                if (success) {
                    lastSnapshotRef.current = currentContent;
                }
            }
        }, frequencyMinutes * 60 * 1000); // Convert minutes to milliseconds

    }, [documentData, frequencyMinutes, enabled]);

    const stopAutoSnapshot = React.useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    const saveManualSnapshot = React.useCallback(async () => {
        if (!documentData.name || !documentData.content) {
            return false;
        }

        const success = await saveDocumentSnapshot(documentData);
        if (success) {
            lastSnapshotRef.current = JSON.stringify(documentData);
        }
        return success;
    }, [documentData]);

    // Cleanup on unmount
    React.useEffect(() => {
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []);

    return {
        startAutoSnapshot,
        stopAutoSnapshot,
        saveManualSnapshot
    };
};